import { lerp, range } from "../morph";
import { EXPERIENCES, YearMonth } from "../../../data/experience";

/*
 * When things happen in the Experience scene, independent of how it's laid
 * out: the playhead's month at each point of scroll, and when each role's lane
 * forks, its card unfolds, and it merges and folds away. The desktop scene
 * (horizontal axis) and the phone scene (vertical axis) share it, so both
 * tell the story to the same beat.
 */

export const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
export const toIndex = (d: YearMonth) => d.year * 12 + d.month - 1;

export const BASE = EXPERIENCES.length ? Math.min(...EXPERIENCES.map((e) => toIndex(e.start))) : 0;
const LAST = EXPERIENCES.length ? Math.max(...EXPERIENCES.map((e) => toIndex(e.end))) : 1;
/** Months from the first start to the last end. */
export const SPAN = LAST - BASE;

const REST = 0.06;
const FORK = 0.08;
const MERGE = 0.1;
const MERGE_LAST = 0.06;
const HOLD = 0.08;

export interface Beat {
    /** First and last month of the role, counted from BASE. */
    m0: number;
    m1: number;
    fork: [number, number];
    head: [number, number];
    body: [number, number];
    merge: [number, number];
    /** Absent for the last role: its card stays open. */
    fold?: [number, number];
    stub?: [number, number];
}

const runMonths = EXPERIENCES.reduce((n, e) => n + toIndex(e.end) - toIndex(e.start), 0);
const runBudget = 1 - REST - HOLD - EXPERIENCES.length * FORK - (EXPERIENCES.length - 1) * MERGE - MERGE_LAST;

/** [p, month] pairs; the playhead is linear between them. */
const KEYS: [number, number][] = [];
export const SCHEDULE: Beat[] = [];
(() => {
    if (!EXPERIENCES.length) return;
    let t = REST;
    KEYS.push([0, toIndex(EXPERIENCES[0].start) - BASE]);
    EXPERIENCES.forEach((e, j) => {
        const m0 = toIndex(e.start) - BASE;
        const m1 = toIndex(e.end) - BASE;
        const last = j === EXPERIENCES.length - 1;
        const fs = t;
        KEYS.push([fs, m0]);
        t += FORK;
        KEYS.push([t, m0]); // hold still while the lane forks
        t += (runBudget * (m1 - m0)) / Math.max(1, runMonths);
        KEYS.push([t, m1]);
        const ms = t;
        SCHEDULE.push({
            m0,
            m1,
            fork: [fs, fs + 0.04],
            head: [fs + 0.02, fs + 0.08],
            body: [fs + 0.05, fs + 0.12],
            merge: [ms, ms + 0.04],
            fold: last ? undefined : [ms + 0.02, ms + 0.07],
            stub: last ? undefined : [ms + 0.05, ms + 0.1],
        });
        if (last) {
            t += MERGE_LAST;
        } else {
            KEYS.push([ms + MERGE * 0.6, m1]); // hold while it merges and folds
            t += MERGE;
            KEYS.push([t, toIndex(EXPERIENCES[j + 1].start) - BASE]);
        }
    });
    KEYS.push([1, SPAN]);
})();

/** The playhead's month (fractional) at scene progress `p`. */
export function monthAt(p: number) {
    for (let i = 1; i < KEYS.length; i++) {
        const [pa, ma] = KEYS[i - 1];
        const [pb, mb] = KEYS[i];
        if (p <= pb) return pb === pa ? mb : lerp(ma, mb, range(p, pa, pb));
    }
    return SPAN;
}

export function labelFor(m: number) {
    const i = BASE + Math.floor(m + 1e-6);
    return { month: (i % 12) + 1, year: Math.floor(i / 12) };
}

export const firstLabel = `${MONTHS[BASE % 12]} ${Math.floor(BASE / 12)}`;
export const lastLabel = `${MONTHS[(BASE + SPAN) % 12]} ${Math.floor((BASE + SPAN) / 12)}`;
